import React from 'react';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SeverityPill } from '../../../components/Redesing/severity-pill';
import CardLoader from '../../../components/Redesing/card-loader';
import { UnlockEventsTable } from '../../../components/Unlocks/UnlockEventsTable';
import { toTitleCase } from '../../../utils';
import { UnlockableHardwareGraphQL } from '../../../hooks/rentals/query/V2';
import {
  UNLOCK_SUCCESS_BACKWARD_WINDOW_SECONDS,
  UNLOCK_SUCCESS_WINDOW_SECONDS,
} from '../../../util/unlockEvents';
import { IRentalUnlockGroup } from './rental-unlock-groups';

function hardwareDisplayName(hardware: UnlockableHardwareGraphQL): string {
  const type = toTitleCase(hardware.hardwareType?.name) || 'Hardware';
  const identifier = hardware.label ?? hardware.number ?? hardware.oldId ?? hardware.id;
  return `${type} ${identifier}`;
}

function UnlockGroupCard({ group }: { group: IRentalUnlockGroup }) {
  const succeededCount = group.attemptCount - group.failedCount;

  return (
    <Card>
      <CardHeader
        title={hardwareDisplayName(group.hardware)}
        action={
          <Stack direction="row" spacing={1} sx={{ pt: 1, pr: 1 }}>
            {group.attemptCount === 0 && (
              <SeverityPill color="info">No attempts</SeverityPill>
            )}
            {succeededCount > 0 && (
              <SeverityPill color="success">{succeededCount} successful</SeverityPill>
            )}
            {group.failedCount > 0 && (
              <SeverityPill color="error">{group.failedCount} failed</SeverityPill>
            )}
          </Stack>
        }
      />
      {group.rows.length === 0 ? (
        <Typography color="text.secondary" sx={{ px: 3, pb: 3 }}>
          No unlock attempts were recorded for this hardware during the rental.
        </Typography>
      ) : (
        <UnlockEventsTable rows={group.rows} />
      )}
    </Card>
  );
}

function RentalUnlocks({
  groups,
  successRate,
  isLoading,
  error,
}: {
  groups: IRentalUnlockGroup[];
  successRate: number | null;
  isLoading: boolean;
  error: Error | null;
}) {
  if (isLoading) {
    return <CardLoader useCard dataId="rental-unlocks-loader" />;
  }

  if (error) {
    return (
      <Card>
        <Typography color="text.secondary" sx={{ p: 3 }}>
          Unlock events could not be loaded for this rental.
        </Typography>
      </Card>
    );
  }

  if (groups.length === 0) {
    return (
      <Card>
        <Typography color="text.secondary" sx={{ p: 3 }}>
          No unlockable hardware was found for this rental.
        </Typography>
      </Card>
    );
  }

  return (
    <Stack spacing={2} data-id="rental-unlocks">
      <Stack spacing={0.5}>
        <Typography variant="h4" data-id="rental-unlock-success-rate">
          {successRate !== null ? `${Number(successRate.toFixed(1))}%` : '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Unlock success rate across all unlockable hardware during the rental. An attempt
          is marked failed when no unlock success was recorded for the same hardware from{' '}
          {UNLOCK_SUCCESS_BACKWARD_WINDOW_SECONDS} seconds before the attempt to{' '}
          {UNLOCK_SUCCESS_WINDOW_SECONDS} seconds after.
        </Typography>
      </Stack>
      {groups.map((group) => (
        <UnlockGroupCard key={group.hardware.id} group={group} />
      ))}
    </Stack>
  );
}

export default RentalUnlocks;
